import { Box, Paper, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getCardBorder, CARD_BORDER_RADIUS } from '../types';

export interface SilenceListSkeletonProps {
  count?: number;
}

/**
 * SilenceListSkeleton - Loading skeleton for the silence list.
 *
 * Displays placeholder content while silences are being fetched.
 */
export function SilenceListSkeleton({ count = 2 }: SilenceListSkeletonProps) {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: 2,
        border: getCardBorder(theme),
        borderRadius: CARD_BORDER_RADIUS,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton animation="wave" variant="circular" width={24} height={24} />
          <Skeleton animation="wave" variant="text" width={150} height={28} />
        </Box>
        <Skeleton animation="wave" variant="rounded" width={120} height={32} />
      </Box>

      {/* Silence Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {Array.from({ length: count }).map((_, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 2,
              border: getCardBorder(theme),
              borderRadius: CARD_BORDER_RADIUS,
              borderLeft: '4px solid',
              borderLeftColor: 'grey.300',
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton animation="wave" variant="circular" width={20} height={20} />
                <Skeleton animation="wave" variant="text" width={120} height={22} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton animation="wave" variant="circular" width={16} height={16} />
                <Skeleton animation="wave" variant="text" width={100} height={18} />
              </Box>
            </Box>

            {/* Progress Bar */}
            <Skeleton
              animation="wave"
              variant="rectangular"
              height={6}
              sx={{ borderRadius: 3, mb: 1.5 }}
            />

            {/* Reason */}
            <Skeleton animation="wave" variant="text" width="60%" height={18} sx={{ mb: 1 }} />

            {/* Footer */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Skeleton animation="wave" variant="text" width={200} height={14} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton animation="wave" variant="rounded" width={80} height={28} />
                <Skeleton animation="wave" variant="rounded" width={80} height={28} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
}

export default SilenceListSkeleton;
